import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { useRecordFirstCalving } from "../api/animalsApi";
import type { AnimalDetail } from "../types";

type Props = {
  accountId: string;
  animal: AnimalDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

// Sanity biológico: el primer parto sucede aproximadamente a los 24 meses.
// Restringir la fecha mínima a birthDate + 18m evita errores groseros de
// digitación; el backend valida solo "no anterior a birthDate".
const MIN_AGE_AT_FIRST_CALVING_MONTHS = 18;

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

export function RecordFirstCalvingDialog({
  accountId,
  animal,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const mutation = useRecordFirstCalving(accountId, animal.id);

  const [occurredAt, setOccurredAt] = useState<Date | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOccurredAt(new Date());
    setError(null);
  }, [open]);

  const birthDate = new Date(animal.birthDate);
  const minDate = addMonths(birthDate, MIN_AGE_AT_FIRST_CALVING_MONTHS);
  const today = new Date();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!occurredAt) {
      setError("Selecciona la fecha del primer parto.");
      return;
    }
    if (occurredAt > today) {
      setError("La fecha no puede estar en el futuro.");
      return;
    }
    if (occurredAt < minDate) {
      setError(
        `La fecha es biológicamente improbable (anterior a ${MIN_AGE_AT_FIRST_CALVING_MONTHS} meses post-nacimiento).`,
      );
      return;
    }

    try {
      await mutation.mutateAsync({ occurredAt: occurredAt.toISOString() });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError("Ocurrió un error inesperado.");
      }
    }
  }

  const pending = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar primer parto</DialogTitle>
          <DialogDescription>
            Este es el primer parto de la novilla. Pasará a Vaca en producción y se iniciará el
            cronograma de depreciación según los valores por defecto de la finca.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="first-calving-date" className="font-medium text-[13px] text-app-ink-2">
              Fecha del primer parto
            </label>
            <DatePicker
              id="first-calving-date"
              aria-label="Fecha del primer parto"
              value={occurredAt}
              onChange={setOccurredAt}
              minDate={minDate}
              maxDate={today}
              disabled={pending}
            />
            <p className="text-[11px] text-app-ink-3">
              Frontera contable: a partir de esta fecha el animal entra al cronograma de
              depreciación.
            </p>
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Cancelar
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
            >
              {pending ? "Registrando…" : "Registrar primer parto"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
